import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useParams } from "react-router-dom";
import reduxStore from "reduxstore/reduxStore";
import { jwtDecode } from "jwt-decode";
import i18n from "i18n/i18n";
import Uri from "jsuri";
import isEmpty from "lodash/isEmpty";
import { SearchSuggestionSelect, Button } from "@texada/ui";
import {
  APP_DEFAULT_REDIRECT_URL,
  HEADOFFICE_OPTION_VALUE
} from "constants/AppConstants";
import { setCurrentUser } from "actions/authActions";
import { AuthorizationToken } from "helpers/AuthorizationToken";
import {
  useLogin,
  switchInstance,
  setDocDivisionAccess,
  getLoginError
} from "./loginUtils";
import MainScreen from "./MainScreen";
import SRMSyncLoader from "./SRMSyncLoader";
import LoginInfoDisplay from "./LoginInfoDisplay";

const SwitchInstance = () => {
  const { id } = useParams();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const user = useSelector(state => state.auth.user);
  const [isSyncingData, setIsSyncingData] = useState(true);
  const { loginAuth } = useLogin(setIsSyncingData);
  const [isLoading, setIsLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedDivision, setSelectedDivision] = useState({});
  const [divisionOptions, setDivisionOptions] = useState([]);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const jwtToken = query.get("token");
    if (!jwtToken) {
      redirectPage(window.location.origin, {
        message: i18n.t(
          "cannot_switch_company_please_contact_Admin_to_migrate_the_account"
        )
      });
      return;
    }
    AuthorizationToken.set(jwtToken);
    const decodedToken = jwtDecode(jwtToken);
    if (!decodedToken.tis_user_id || !decodedToken?.company_sso_enabled) {
      const instanceError = i18n.t(
        "cannot_switch_company_please_contact_Admin_to_migrate_the_account"
      );
      if (query.get("callbackUrl")) {
        window.location.href = new Uri(query.get("callbackUrl"))
          .addQueryParam("error", instanceError)
          .toString();
        return;
      }
      setIsSyncingData(false);
      setErrors({
        instanceError
      });
      return;
    }
    if (Number(decodedToken.tenant_id) === Number(id)) {
      redirectPage();
      return;
    }
    onSwitchInstance(decodedToken);
  }, []);

  useEffect(() => {
    if (Number(user?.tenant_id) !== Number(id)) return;
    configOperatorByDivision();
  }, [user?.tenant_id]);

  const onSwitchInstance = async userData => {
    if (userData.company_upgraded) {
      await switchInstance(userData.user_id, userData.tenant_id, id);
    }

    try {
      await loginAuth(
        id,
        undefined,
        userData.tis_session,
        userData.company_sso_enabled,
        userData.email
      );
      localStorage.setItem("verified", true);
    } catch (err) {
      redirectPage(window.location.origin, err);
      return;
    }
  };

  const configOperatorByDivision = async () => {
    if (!user.compperms.operator_by_division || query.get("callbackUrl")) {
      const userInfo = await setDocDivisionAccess(user);
      await reduxStore.dispatch(setCurrentUser(userInfo));
      redirectPage();
      return;
    }
    setIsSyncingData(false);
    fetchDivisions();
    fetchPreSelected();
  };

  const fetchDivisions = () => {
    let userDivisions = user?.divisions;
    if (user?.upgraded) {
      userDivisions = user?.divisions?.filter(e => e.is_user_division);
    }
    setDivisionOptions(
      userDivisions.map(division => ({
        label: division.warehouse.title,
        value: division.warehouse.srm_uid || HEADOFFICE_OPTION_VALUE,
        srm_uid: division.warehouse.srm_uid
      }))
    );
  };

  const fetchPreSelected = () => {
    setSearch("");
    setSelectedDivision([]);
    const docAccess = JSON.parse(localStorage.getItem("docDivisionAccess"));

    if (isEmpty(docAccess?.[user.tenant_id])) return;

    const docDivision = docAccess[user.tenant_id].divisions.find(
      e => e.is_doc_division
    );
    const target = user?.divisions?.find(
      e => e.warehouse.srm_uid === docDivision.srm_uid
    );

    if (!target) return;

    setSelectedDivision({
      srm_uid: target.warehouse.srm_uid,
      label: target.warehouse.title,
      value: target.warehouse.srm_uid || HEADOFFICE_OPTION_VALUE
    });
    setSearch(target.warehouse.title);
  };

  const onSubmit = async () => {
    setIsLoading(true);
    try {
      const userInfo = await setDocDivisionAccess(
        user,
        selectedDivision.srm_uid || HEADOFFICE_OPTION_VALUE
      );
      await reduxStore.dispatch(setCurrentUser(userInfo));
      setIsLoading(false);
      redirectPage();
    } catch (err) {
      setIsLoading(false);
    }
  };

  const redirectPage = (url = APP_DEFAULT_REDIRECT_URL, error = undefined) => {
    if (query.get("callbackUrl")) {
      const redirectUrl = new Uri(query.get("callbackUrl"));
      if (localStorage.getItem("jwtToken")) {
        redirectUrl.addQueryParam("token", localStorage.getItem("jwtToken"));
      }
      if (error) {
        redirectUrl.addQueryParam("error", getLoginError(error));
      }
      window.location.href = redirectUrl.toString();
      return;
    }
    if (error) {
      localStorage.setItem("login-error", getLoginError(error));
    }
    window.location.href = url;
  };

  if (errors?.instanceError) {
    return (
      <MainScreen>
        <LoginInfoDisplay errorMsg={errors?.instanceError} />
      </MainScreen>
    );
  }

  return (
    <MainScreen>
      {isSyncingData ? (
        <SRMSyncLoader message={i18n.t("switching_instance")} />
      ) : (
        <>
          <SearchSuggestionSelect
            required
            label={i18n.t("division")}
            placeholder={i18n.t("select_document_division")}
            inputValue={search}
            onInputValueChange={setSearch}
            options={divisionOptions}
            selectedItem={selectedDivision?.value}
            onChange={setSelectedDivision}
            error={errors?.division}
          />
          <Button
            className="login-button"
            isLoading={isLoading}
            onClick={onSubmit}
          >
            {i18n.t("confirm")}
          </Button>
        </>
      )}
    </MainScreen>
  );
};

export default SwitchInstance;
